/**
 * Dynatrace Custom RUM Events
 *
 * This module provides utilities for emitting custom events to Dynatrace RUM
 * to track specific user interactions and application states.
 */

import { getDynatraceAPI, isDynatraceLoaded } from './dynatrace';
import {
	ScreenLoadEventProperties,
	DataDisappearedEventProperties,
} from './types';

/**
 * Context for getting user and tenant information
 * This can be provided by the consuming application
 */
let userContextProvider:
	| (() => { userId: string; tenantId: string; appVersion?: string })
	| undefined;

/**
 * Set the user context provider function
 * Call this from your app to provide dynamic user/tenant information
 *
 * @param provider - Function that returns user context
 */
export const setUserContextProvider = (
	provider: () => { userId: string; tenantId: string; appVersion?: string },
): void => {
	userContextProvider = provider;
};

/**
 * Get user context properties for correlation
 */
const getUserContextProperties = (): Record<string, string> => {
	try {
		if (userContextProvider) {
			const context = userContextProvider();
			return {
				userId: context.userId || 'unknown',
				tenantId: context.tenantId || 'unknown',
				appVersion: context.appVersion || 'unknown',
			};
		}

		return {
			userId: 'unknown',
			tenantId: 'unknown',
			appVersion: 'unknown',
		};
	} catch (error) {
		console.warn(
			'[Dynatrace] Failed to get user context properties',
			error,
		);
		return {
			userId: 'unknown',
			tenantId: 'unknown',
			appVersion: 'unknown',
		};
	}
};

/**
 * Emit a screen_load custom event to Dynatrace
 *
 * Tracks when a screen becomes interactive/usable
 *
 * @param properties - Screen load event properties
 */
export const emitScreenLoadEvent = (
	properties: ScreenLoadEventProperties,
): void => {
	if (!isDynatraceLoaded()) {
		console.warn(
			'[Dynatrace] RUM not loaded, cannot emit screen_load event',
		);
		return;
	}

	// Validate required properties
	if (!properties.screen || typeof properties.screen !== 'string') {
		console.warn(
			'[Dynatrace] Invalid screen name provided to emitScreenLoadEvent',
		);
		return;
	}

	try {
		const dtrum = getDynatraceAPI();

		// Get user context for correlation
		const userContext = getUserContextProperties();

		// Prioritize dtrum.enterAction as it creates visible user actions in Dynatrace UI
		if (dtrum && typeof dtrum.enterAction === 'function') {
			const actionName = `screen_load_${properties.screen}`;
			const actionId = dtrum.enterAction(actionName); // actionType parameter is deprecated

			// Add properties to the action
			if (typeof dtrum.addActionProperties === 'function') {
				dtrum.addActionProperties(actionId, {
					screen: properties.screen,
					route: properties.route,
					userId: userContext.userId,
					tenantId: userContext.tenantId,
					appVersion: userContext.appVersion,
					durationMs: properties.durationMs,
					...(properties.assetCount !== undefined && {
						assetCount: properties.assetCount,
					}),
				});
			}

			dtrum.leaveAction(actionId);
		} else if (typeof window.dynatrace?.sendEvent === 'function') {
			// Fallback to sendEvent API
			const eventName = `screen_load_${properties.screen}`;

			// Build event properties object
			const eventProperties: Record<string, string | number> = {
				screen: properties.screen,
				route: properties.route,
				userId: userContext.userId,
				tenantId: userContext.tenantId,
				appVersion: userContext.appVersion,
				eventType: 'screen_load',
				durationMs: properties.durationMs,
			};

			// Add asset count if provided
			if (properties.assetCount !== undefined) {
				eventProperties['assetCount'] = properties.assetCount;
			}

			// Add any additional properties
			Object.entries(properties).forEach(([key, value]) => {
				if (
					value !== undefined &&
					!['screen', 'route', 'durationMs', 'assetCount'].includes(
						key,
					)
				) {
					eventProperties[key] = value;
				}
			});

			// Use sendEvent API with event name and properties
			window.dynatrace.sendEvent(eventName, eventProperties);
		} else {
			console.warn(
				'[Dynatrace] No Dynatrace API available (dtrum or sendEvent)',
			);
			return;
		}

		console.info('[Dynatrace] screen_load event emitted');
	} catch (error) {
		console.error('[Dynatrace] Failed to emit screen_load event', error);
	}
};

// Recursion guard for Dynatrace API calls
let isEmittingEvent = false;

/**
 * Emit a data_disappeared custom event to Dynatrace
 *
 * Tracks when data goes from populated to empty state
 *
 * @param properties - Data disappearance event properties
 */
export const emitDataDisappearedEvent = (
	properties: DataDisappearedEventProperties,
): void => {
	// Prevent infinite recursion
	if (isEmittingEvent) {
		console.warn(
			'[Dynatrace] Already emitting event, skipping to prevent recursion',
		);
		return;
	}

	if (!isDynatraceLoaded()) {
		console.warn(
			'[Dynatrace] RUM not loaded, cannot emit data_disappeared event',
		);
		return;
	}

	// Validate required properties
	if (!properties.screen || typeof properties.screen !== 'string') {
		console.warn(
			'[Dynatrace] Invalid screen name provided to emitDataDisappearedEvent',
		);
		return;
	}

	// Set recursion guard
	isEmittingEvent = true;

	try {
		const dtrum = getDynatraceAPI();

		// Get user context for correlation
		const userContext = getUserContextProperties();

		// Prioritize dtrum.enterAction as it creates visible user actions in Dynatrace UI
		if (dtrum && typeof dtrum.enterAction === 'function') {
			const actionName = `data_disappeared_${properties.screen}_${properties.reason}`;

			try {
				const actionId = dtrum.enterAction(actionName); // actionType parameter is deprecated

				// Validate actionId
				if (!actionId) {
					console.error(
						'[Dynatrace] enterAction returned invalid actionId',
					);
					return;
				}

				// Add properties to the action
				if (typeof dtrum.addActionProperties === 'function') {
					try {
						// Convert all values to strings for Dynatrace
						dtrum.addActionProperties(actionId, {
							screen: String(properties.screen),
							route: String(properties.route || ''),
							reason: String(properties.reason),
							userId: String(userContext.userId),
							tenantId: String(userContext.tenantId),
							appVersion: String(userContext.appVersion),
							previousRecordCount: String(
								properties.previousRecordCount || 0,
							),
							timestamp: String(
								properties.timestamp || Date.now(),
							),
						});
					} catch (propError) {
						console.error(
							'[Dynatrace] Failed to add action properties',
							propError,
						);
						// Continue even if properties fail
					}
				}

				if (typeof dtrum.leaveAction === 'function') {
					dtrum.leaveAction(actionId);
				}

				console.log(`[Dynatrace] Created user action: ${actionName}`);
			} catch (actionError) {
				console.error(
					'[Dynatrace] Error in enterAction/leaveAction',
					actionError,
				);
				throw actionError; // Re-throw to be caught by outer catch
			}
		} else if (typeof window.dynatrace?.sendEvent === 'function') {
			// Fallback to sendEvent API
			const eventName = `data_disappeared_${properties.screen}_${properties.reason}`;

			try {
				// Build event properties object - convert all to strings
				const eventProperties: Record<string, string> = {
					screen: String(properties.screen),
					route: String(properties.route || ''),
					reason: String(properties.reason),
					userId: String(userContext.userId),
					tenantId: String(userContext.tenantId),
					appVersion: String(userContext.appVersion),
					eventType: 'data_disappeared',
					previousRecordCount: String(
						properties.previousRecordCount || 0,
					),
					timestamp: String(properties.timestamp || Date.now()),
				};

				// Add any additional properties
				Object.entries(properties).forEach(([key, value]) => {
					if (
						value !== undefined &&
						![
							'screen',
							'route',
							'reason',
							'previousRecordCount',
							'timestamp',
						].includes(key)
					) {
						eventProperties[key] = String(value);
					}
				});

				// Use sendEvent API with event name and properties
				window.dynatrace.sendEvent(eventName, eventProperties);
				console.log(`[Dynatrace] Sent event: ${eventName}`);
			} catch (sendError) {
				console.error('[Dynatrace] Error in sendEvent', sendError);
				throw sendError; // Re-throw to be caught by outer catch
			}
		} else {
			console.warn(
				'[Dynatrace] No Dynatrace API available (dtrum or sendEvent)',
			);
			return;
		}

		console.info('[Dynatrace] data_disappeared event emitted', {
			screen: properties.screen,
			previousRecordCount: properties.previousRecordCount,
			reason: properties.reason,
			userId: userContext.userId,
			tenantId: userContext.tenantId,
		});
	} catch (error) {
		console.error(
			'[Dynatrace] Failed to emit data_disappeared event',
			error,
		);
	} finally {
		// Always clear recursion guard
		isEmittingEvent = false;
	}
};

/**
 * Hook to track screen load performance
 * Returns a function to call when screen is ready
 */
export const createScreenLoadTracker = (screenName: string) => {
	const startTime = performance.now();

	return (additionalProperties?: Partial<ScreenLoadEventProperties>) => {
		const durationMs = Math.round(performance.now() - startTime);

		emitScreenLoadEvent({
			screen: screenName,
			durationMs,
			route: window.location.pathname,
			...additionalProperties,
		});
	};
};
